import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  AlertTriangleIcon,
  CheckCircle2Icon,
  FileMinusIcon,
  FileTextIcon,
  NetworkIcon,
  RefreshCwIcon,
  XCircleIcon,
} from "lucide-react";
import { api } from "@/api/client";
import type { components } from "@dolfin/api-admin";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { CopyButton } from "@/components/ui/copy-button";
import { Spinner } from "@/components/ui/spinner";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { ProblemDetailsAlert } from "@/components/ProblemDetailsAlert";
import { formatDateTime } from "@/lib/format";
import type { ProblemDetails } from "@/lib/problemDetails";

type Registration = components["schemas"]["EInvoicingRegistrationResponse"];
type RegistrationStatus = components["schemas"]["EInvoicingRegistrationStatus"];
type ReceivedDocument = components["schemas"]["OrganisationEInvoiceResponse"];

const statusVariant: Record<
  NonNullable<RegistrationStatus>,
  "default" | "secondary" | "destructive" | "outline"
> = {
  Registered: "default",
  Submitted: "secondary",
  Processing: "secondary",
  ActionRequired: "secondary",
  NotStarted: "outline",
  Failed: "destructive",
  Unregistering: "secondary",
  Unregistered: "outline",
};

/** What each status means for the reader: ActionRequired waits on the customer, Failed on the provider. */
const statusMeaning: Record<NonNullable<RegistrationStatus>, string> = {
  NotStarted: "Never submitted. Nothing has been sent to the provider for this organisation.",
  Submitted: "Sent to the provider and awaiting its first response.",
  ActionRequired: "Stalled on the customer. The actions below are what they still owe.",
  Processing: "With the provider. It completes on its own; nothing to do here.",
  Registered: "Live. Documents addressed to the participant id below reach this organisation.",
  Failed: "The provider rejected it. Its own words are below.",
  Unregistering:
    "Being withdrawn from the network. Still reachable until the provider confirms; a refusal puts it back to Registered with the reason below.",
  Unregistered:
    "Withdrawn. Nothing is delivered to it any more, its address is released and its tax number can be edited. It can register again.",
};

/** Decimals cross the wire as strings on some routes and numbers on others, so both are handled. */
function formatMoney(
  amount: string | number | null | undefined,
  currency: string | null | undefined,
) {
  if (amount === null || amount === undefined) return "-";
  const value = typeof amount === "string" ? Number(amount) : amount;
  if (!Number.isFinite(value)) return "-";
  return `${value.toFixed(2)} ${currency ?? ""}`.trim();
}

/** What the document became: exactly one of billId / supplierCreditNoteId is set once ingested. Credit notes have no page here yet. */
function ingestedAs(doc: ReceivedDocument) {
  if (doc.supplierCreditNoteId) {
    return {
      label: "Credit note",
      id: doc.supplierCreditNoteId,
      to: null,
      Icon: FileMinusIcon,
    };
  }
  if (doc.billId) {
    return {
      label: "Bill",
      id: doc.billId,
      to: `/bills/${doc.billId}`,
      Icon: FileTextIcon,
    };
  }
  return null;
}

interface OrganisationEInvoicingSectionProps {
  organisationId: string;
}

export function OrganisationEInvoicingSection({
  organisationId,
}: OrganisationEInvoicingSectionProps) {
  const [registration, setRegistration] = useState<Registration | null>(null);
  const [documents, setDocuments] = useState<ReceivedDocument[]>([]);
  const [loading, setLoading] = useState(true);
  const [problem, setProblem] = useState<ProblemDetails | null>(null);
  const [documentsError, setDocumentsError] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setProblem(null);
    setDocumentsError(false);

    // Admin routes, not the organisation's own: those are module-gated against the organisation's
    // client, and staff need to see the registration and documents whatever the client has bought.
    const [status, received] = await Promise.all([
      api.GET("/admin/organisations/{organisationId}/e-invoicing", {
        params: { path: { organisationId } },
      }),
      // Explicit limit (the default is 50) as there's no pager; beyond 200, use the paginated Received e-invoices queue.
      api.GET("/admin/organisations/{organisationId}/received-e-invoices", {
        params: { path: { organisationId }, query: { limit: 200 } },
      }),
    ]);

    if (status.error) {
      setProblem(status.error as ProblemDetails);
      setRegistration(null);
    } else {
      setRegistration(status.data ?? null);
    }

    // The calls fail independently, so a broken list doesn't blank the status above it.
    if (received.error) {
      setDocumentsError(true);
      setDocuments([]);
    } else {
      setDocuments(received.data?.data ?? []);
    }

    setLoading(false);
  }, [organisationId]);

  useEffect(() => {
    load();
  }, [load]);

  if (loading && !registration) {
    return (
      <div className="flex justify-center py-8">
        <Spinner className="size-5" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-end">
        <Button variant="outline" size="sm" onClick={load} disabled={loading}>
          <RefreshCwIcon className="size-4" />
          Refresh
        </Button>
      </div>

      <ProblemDetailsAlert problem={problem} fallback="Failed to load e-invoicing status." />

      {registration && <RegistrationCard registration={registration} />}

      <div>
        <h3 className="mb-3 text-sm font-semibold uppercase tracking-wider text-muted-foreground">
          Received Documents
        </h3>
        {documentsError ? (
          <p className="text-destructive text-sm">Failed to load received documents.</p>
        ) : (
          <ReceivedDocumentsTable documents={documents} />
        )}
      </div>
    </div>
  );
}

function RegistrationCard({ registration }: { registration: Registration }) {
  const status = registration.status;
  const participant = registration.participant;
  const party = registration.party;
  const requiredActions = registration.requiredActions ?? [];
  const faults = registration.faults ?? [];

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle>Registration</CardTitle>
        {status && <Badge variant={statusVariant[status] ?? "secondary"}>{status}</Badge>}
      </CardHeader>
      <CardContent className="space-y-6">
        {status && <p className="text-sm text-muted-foreground">{statusMeaning[status]}</p>}

        <dl className="grid [&>div]:min-w-0 grid-cols-1 gap-4 sm:grid-cols-3">
          <div>
            <dt className="text-muted-foreground text-sm">Network</dt>
            <dd className="mt-1 text-sm">
              {registration.network ? (
                <span className="inline-flex items-center gap-1.5">
                  <NetworkIcon className="size-3.5" />
                  {registration.network}
                </span>
              ) : (
                <span className="text-muted-foreground">-</span>
              )}
            </dd>
          </div>
          <div>
            {/* The participant id is the address senders use; copyable to compare against an unroutable delivery. */}
            <dt className="text-muted-foreground text-sm">Participant ID</dt>
            <dd className="mt-1 flex items-center gap-1">
              {participant ? (
                <>
                  <Badge variant="secondary" className="font-mono text-xs">
                    {participant.scheme}:{participant.code}
                  </Badge>
                  <CopyButton value={`${participant.scheme}:${participant.code}`} />
                </>
              ) : (
                <span className="text-sm text-muted-foreground">not assigned yet</span>
              )}
            </dd>
          </div>
          <div>
            <dt className="text-muted-foreground text-sm">Registration ID</dt>
            <dd className="mt-1 flex items-center gap-1">
              {registration.id ? (
                <>
                  <span className="font-mono text-xs">{registration.id}</span>
                  <CopyButton value={registration.id} />
                </>
              ) : (
                <span className="text-sm text-muted-foreground">-</span>
              )}
            </dd>
          </div>
          <div>
            <dt className="text-muted-foreground text-sm">Submitted</dt>
            <dd className="mt-1 text-sm">
              {registration.submittedAt ? (
                formatDateTime(registration.submittedAt)
              ) : (
                <span className="text-muted-foreground">-</span>
              )}
            </dd>
          </div>
          <div>
            <dt className="text-muted-foreground text-sm">Registered</dt>
            <dd className="mt-1 text-sm">
              {registration.registeredAt ? (
                formatDateTime(registration.registeredAt)
              ) : (
                <span className="text-muted-foreground">-</span>
              )}
            </dd>
          </div>
          <div>
            {/* Only meaningful before a registration exists. */}
            <dt className="text-muted-foreground text-sm">Details complete</dt>
            <dd className="mt-1 flex items-center gap-2 text-sm">
              {status === "NotStarted" ? (
                registration.canSubmit ? (
                  <>
                    <CheckCircle2Icon className="size-4 text-green-600" />
                    Enough to submit
                  </>
                ) : (
                  <>
                    <XCircleIcon className="size-4 text-muted-foreground" />
                    Missing details
                  </>
                )
              ) : (
                <span className="text-muted-foreground">-</span>
              )}
            </dd>
          </div>
        </dl>

        {/* Kept apart: an action is owed by the customer, a fault is the provider refusing. */}
        {requiredActions.length > 0 && (
          <div className="rounded-md border border-amber-500/30 bg-amber-500/5 p-3">
            <h4 className="mb-2 text-xs font-bold uppercase tracking-wider text-muted-foreground">
              Outstanding Actions
            </h4>
            <ul className="space-y-2">
              {requiredActions.map((action) => (
                <li key={action.code} className="flex items-start gap-2 text-sm">
                  <AlertTriangleIcon className="mt-0.5 size-4 shrink-0 text-amber-600" />
                  <span>
                    {action.detail}
                    <span className="ml-2 font-mono text-xs text-muted-foreground">
                      {action.code}
                    </span>
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {faults.length > 0 && (
          <div>
            <h4 className="mb-2 text-xs font-bold uppercase tracking-wider text-muted-foreground">
              Provider Faults
            </h4>
            <ul className="list-disc space-y-1 pl-5">
              {faults.map((fault, i) => (
                <li key={i} className="font-mono text-xs text-destructive">
                  {fault.message ?? fault.code}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Submitted details: a tax number not matching the provider's is the usual cause of Failed/ActionRequired. */}
        <div>
          <h4 className="mb-2 text-xs font-bold uppercase tracking-wider text-muted-foreground">
            {status === "NotStarted" ? "Would Submit" : "Submitted Details"}
          </h4>
          <dl className="grid [&>div]:min-w-0 grid-cols-1 gap-4 text-sm sm:grid-cols-3">
            <PartyField label="Legal name" value={party.legalName} />
            <PartyField label="Trading name" value={party.tradingName} />
            <PartyField label="Tax number" value={party.taxNumber} mono />
            <PartyField
              label="Company registration"
              value={party.companyRegistrationNumber}
              mono
            />
            <PartyField label="Contact email" value={party.contactEmail} />
            <PartyField label="Country" value={party.country} />
            <div className="sm:col-span-3">
              <dt className="text-muted-foreground text-sm">Registered address</dt>
              <dd className="mt-1">
                {[
                  party.addressLine1,
                  party.addressLine2,
                  party.city,
                  party.addressRegion,
                  party.postalCode,
                ]
                  .filter(Boolean)
                  .join(", ") || <span className="text-muted-foreground">-</span>}
              </dd>
            </div>
          </dl>
        </div>
      </CardContent>
    </Card>
  );
}

function PartyField({
  label,
  value,
  mono,
}: {
  label: string;
  value: string | null | undefined;
  mono?: boolean;
}) {
  return (
    <div>
      <dt className="text-muted-foreground text-sm">{label}</dt>
      <dd className={`mt-1 ${mono ? "font-mono text-xs" : ""}`}>
        {value || <span className="font-sans text-sm text-muted-foreground">-</span>}
      </dd>
    </div>
  );
}

function ReceivedDocumentsTable({ documents }: { documents: ReceivedDocument[] }) {
  if (documents.length === 0) {
    return (
      <p className="rounded-md border border-dashed p-8 text-center text-sm text-muted-foreground">
        No documents have been delivered to this organisation over a network yet.
      </p>
    );
  }

  return (
    <div className="rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>State</TableHead>
            <TableHead>Supplier</TableHead>
            <TableHead>Document</TableHead>
            <TableHead className="text-right">Amount</TableHead>
            <TableHead>Received</TableHead>
            <TableHead>Became</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {documents.map((doc) => {
            const outcome = ingestedAs(doc);
            return (
              <TableRow key={doc.id}>
                <TableCell className="align-top">
                  <Badge
                    variant={doc.state === "Ingested" ? "default" : "secondary"}
                    className={
                      doc.state === "Rejected"
                        ? "border-amber-500/40 bg-amber-500/10 text-amber-700 dark:text-amber-400"
                        : undefined
                    }
                  >
                    {doc.state}
                  </Badge>
                </TableCell>
                <TableCell className="max-w-[16rem] align-top">
                  <div className="truncate">{doc.supplierName ?? "-"}</div>
                  {doc.supplierElectronicAddress && (
                    <div className="truncate font-mono text-xs text-muted-foreground">
                      {doc.supplierElectronicAddress}
                    </div>
                  )}
                </TableCell>
                <TableCell className="align-top">
                  <div className="font-mono text-xs">{doc.invoiceNumber ?? "-"}</div>
                  {/* No bill exists for a rejected document, so this row is the only account of why it stopped. */}
                  {doc.faults && doc.faults.length > 0 && (
                    <ul className="mt-1 space-y-1">
                      {doc.faults.map((fault, i) => (
                        <li
                          key={i}
                          className="flex items-start gap-1.5 text-xs text-muted-foreground"
                        >
                          <AlertTriangleIcon
                            className={
                              fault.severity === "Stop"
                                ? "mt-0.5 size-3 shrink-0 text-red-600"
                                : "mt-0.5 size-3 shrink-0 text-amber-600"
                            }
                          />
                          <span>{fault.message}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </TableCell>
                <TableCell className="text-right align-top tabular-nums">
                  {formatMoney(doc.totalAmount, doc.currency)}
                </TableCell>
                <TableCell className="align-top text-xs text-muted-foreground">
                  {doc.receivedAt ? formatDateTime(doc.receivedAt) : "-"}
                </TableCell>
                <TableCell className="align-top">
                  {outcome ? (
                    outcome.to ? (
                      <Link
                        to={outcome.to}
                        className="inline-flex items-center gap-1 text-sm text-primary hover:underline"
                      >
                        <outcome.Icon className="size-3.5" />
                        {outcome.label}
                      </Link>
                    ) : (
                      // No supplier-credit-note page in this dash yet, so the id is offered to
                      // copy rather than linked to a route that would 404.
                      <span className="inline-flex items-center gap-1 text-sm">
                        <outcome.Icon className="size-3.5 text-muted-foreground" />
                        {outcome.label}
                        <CopyButton value={outcome.id} />
                      </span>
                    )
                  ) : (
                    <span className="text-sm text-muted-foreground">nothing</span>
                  )}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}
